import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import {
  Button,
  Flex,
  FormHeading,
  Grid,
  Label,
  Text,
  FileUpload,
  Box,
  ErrorMsg,
  Error,
  Toastify,
} from "atoms";
import {
  FormCheckbox,
  FormInput,
  FormRadio,
  FormSelect,
} from "molecules/FormInput";
import MemoClose from "assets/icons/Close";
import MemoAdd from "assets/icons/Add";
import { optionFetch } from "utils/utilities";
import { usePropsoch } from "contexts/Propsoch";
import { useParams } from "react-router-dom";

const defTransObj = {
  nearbyTransValues: [
    {
      nearbyTransValueId: "",
      images: [],
      distanceToProject: "",
    },
  ],
};
const defSubTransObj = {
  nearbyTransValueId: "",
  images: [],
  distanceToProject: "",
};

export const ConnectivityForm = ({
  onNext,
  onPrevious,
  bindHitOnSave,
  fetchProperty,
  rootProperty,
}) => {
  const {
    actions: {
      getPropertyListingConnectivity,
      createPropertyListingConnectivity,
      updatePropertyListingConnectivity,
    },
  } = usePropsoch();

  const { id } = useParams();

  // fetch property
  const [property, setProperty] = useState(null);

  useEffect(() => {
    id &&
      getPropertyListingConnectivity(id)?.then((data) => {
        setProperty(data);
        const chosenTransportation = [];
        let chosenTransportationData = {};
        data?.transportations?.forEach((trans) => {
          // set trans
          chosenTransportation?.push(trans?.nearbyTransId);

          // set trans data
          chosenTransportationData[`${trans?.nearbyTransId?.toString()}`] = {};
          chosenTransportationData[
            `${trans?.nearbyTransId?.toString()}`
          ].nearbyTransValues = trans?.nearbyTransValues?.map(
            (nearbyTransValue) => ({
              nearbyTransValueId: {
                value: nearbyTransValue?.nearbyTransValue?.nearbyTransValueId,
                label: nearbyTransValue?.nearbyTransValueName,
              },
              images: nearbyTransValue?.images,
              distanceToProject:
                nearbyTransValue?.nearbyTransValue?.distanceToProject,
            })
          );
        });

        // reformat type
        data &&
          setProperty((prev) => ({
            ...prev,
            // trans
            chosenTransportation: chosenTransportation,
            // trans data
            chosenTransportationData: chosenTransportationData,
          }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = useCallback(
    ({
      accessRoadWidth,
      qualityOfAccessRoad,
      roadImages,
      chosenTransportation,
      chosenTransportationData,
      connectivityComments,
      dirty,
    }) => {
      setSubmitLoading(true);
      if (dirty) {
        // new nearbyAreas total
        const transportations = chosenTransportation?.map((item) => ({
          nearbyTransId: item,
          nearbyTransValues:
            chosenTransportationData?.[
              item?.toString()
            ]?.nearbyTransValues?.map((nearbyTransValue) => ({
              nearbyTransValueId: nearbyTransValue?.nearbyTransValueId?.value,
              distanceToProject: nearbyTransValue?.distanceToProject,
              // images: nearbyTransValue?.images,
            })) || [],
        }));

        const formattedValues = {
          propertyId: id,
          accessRoadWidth,
          roadImages,
          qualityOfAccessRoad,
          transportations,
          connectivityComments,
        };

        if (property?.accessRoadWidth) {
          updatePropertyListingConnectivity(
            { ...formattedValues },
            id,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Connectivity updated");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Connectivity not updated");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          createPropertyListingConnectivity(formattedValues, rootProperty?.name)
            .then(() => {
              fetchProperty()?.then(() => {
                Toastify("success", "Connectivity created");
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Connectivity not created");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        }
      } else {
        if (id) {
          onNext();
        }
      }
    },
    [
      createPropertyListingConnectivity,
      id,
      updatePropertyListingConnectivity,
      onNext,
      property,
      rootProperty,
      fetchProperty,
    ]
  );

  // nearby area
  const [nearbyTransportation, setNearbyTransportation] = useState([]);
  useEffect(() => {
    // nearby area
    optionFetch("/nearbytrans/get", "name", "id").then((data) => {
      setNearbyTransportation(data);
    });
  }, []);

  const validationSchema = Yup.object().shape({
    // accessRoadWidth: Yup.number().required("Access road width is required"),
    connectivityComments: Yup.string()
      .typeError("Comments are required")
      .required("Comments are required"),
    chosenTransportation: Yup.array()
      .min(1, "Choose a transportation")
      .typeError("Transporation is required")
      .required("Transporation is required"),
    accessRoadWidth: Yup.number()
      .min(0, "Value should be greater than 0")
      .typeError("Invalid number")
      .required("Access road width is required"),
    qualityOfAccessRoad: Yup.string()
      .typeError("Quality of access road is required")
      .required("Quality of access road is required"),
    roadImages: Yup.array()
      .min(1, "Add minimum 1 image")
      .typeError("Road image is required")
      .required("Road image is required"),
    chosenTransportationData: Yup.lazy((v) => {
      const inKeysSchemaObjectShape = Object.keys(v || {})?.reduce(
        (pre, cur) => ({
          ...pre,
          [cur?.toString()]: Yup.object().shape({
            nearbyTransValues: Yup.array()
              .of(
                Yup.object().shape({
                  nearbyTransValueId: Yup.object().shape({
                    label: Yup.string().required(
                      `Choose a ${
                        nearbyTransportation?.find(
                          (i) => i?.value === parseInt(cur || 0)
                        )?.label
                      }`
                    ),
                  }),
                  distanceToProject: Yup.number()
                    .min(0, "Value should be greater than 0")
                    .typeError("Invalid number")
                    .required("Distance is required"),
                })
              )
              .min(
                1,
                `Choose a ${
                  nearbyTransportation?.find(
                    (i) => i?.value === parseInt(cur || 0)
                  )?.label
                }`
              ),
          }),
        }),
        {}
      );

      return Yup.object().shape({ ...inKeysSchemaObjectShape });
    }),
  });

  return (
    <Formik
      initialValues={
        property
          ? property
          : {
              accessRoadWidth: "",
              qualityOfAccessRoad: "",
              roadImages: [],
              chosenTransportation: "",
              chosenTransportationData: {},
              connectivityComments: "",
            }
      }
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, submitForm, touched, errors, dirty }) => {
        bindHitOnSave(() => {
          setFieldValue("dirty", dirty);
          submitForm();
        });

        return (
          <Form>
            <Grid gridGap="2.4rem">
              <FormHeading text="Connectivity" />
              <Label text="Nearby Transportation Facilities" req />
              <Grid
                gridAutoFlow="column"
                gridGap="3rem"
                justifyContent="start"
                mb="1rem"
              >
                {nearbyTransportation?.map((item, index) => (
                  <Flex aI="center" key={index}>
                    <FormCheckbox
                      name="chosenTransportation"
                      height="1.8rem"
                      value={item?.value}
                      onChange={(isChecked) => {
                        if (isChecked) {
                          setFieldValue(
                            `chosenTransportationData.${item?.value?.toString()}`,
                            defTransObj
                          );
                        } else {
                          setFieldValue(
                            `chosenTransportationData.${item?.value?.toString()}`,
                            undefined
                          );
                        }
                      }}
                    />
                    <Text color="neutral.700">{item?.label}</Text>
                  </Flex>
                ))}
              </Grid>
              {touched?.chosenTransportation &&
                errors?.chosenTransportation && (
                  <ErrorMsg text={errors?.chosenTransportation} />
                )}

              {Array.isArray(values?.chosenTransportation) &&
                values?.chosenTransportation?.map(
                  (currentTransportation, curIndex) => (
                    <Grid
                      border="1px solid"
                      borderColor="neutral.200"
                      p="2.4rem"
                      gridRowGap="2.4rem"
                      borderRadius="1rem"
                      key={curIndex}
                    >
                      {values?.chosenTransportationData?.[
                        currentTransportation?.toString()
                      ]?.nearbyTransValues?.map((subTrans, subIndex) => (
                        <Box key={subIndex}>
                          {/* <Flex jC="space-between">
                            <Box />
                            {subIndex > 0 && (
                              <Flex
                                alignItems="center"
                                color="accents.100"
                                cursor="pointer"
                                onClick={() => {
                                  const duplicate = JSON.parse(
                                    JSON.stringify(
                                      values?.chosenTransportationData?.[
                                        currentTransportation?.toString()
                                      ]?.nearbyTransValues
                                    )
                                  );
                                  setFieldValue(
                                    `chosenTransportationData.[${currentTransportation?.toString()}].nearbyTransValues`,
                                    [
                                      ...duplicate.slice(0, subIndex),
                                      ...duplicate.slice(subIndex + 1),
                                    ]
                                  );
                                }}
                              >
                                <MemoClose height="1.3rem" width="1.3rem" />
                                <Text
                                  color="accents.100"
                                  fontFamily="NeometricBold"
                                  ml="1rem"
                                >
                                  Remove
                                </Text>
                              </Flex>
                            )}
                          </Flex> */}
                          <Grid
                            gridTemplateColumns="1fr 1fr"
                            gridGap="2.4rem"
                            pb="2.4rem"
                          >
                            <Label
                              text={`${
                                nearbyTransportation?.find(
                                  (i) => i?.value === currentTransportation
                                )?.label
                              } ${subIndex + 1}`}
                              {...(!subIndex && { req: true })}
                              // mb="1.6rem"
                            />
                            <Flex jC="space-between">
                              <Label
                                text={`Distance to Project (in kms)`}
                                {...(!subIndex && { req: true })}
                                // mb="1.6rem"
                              />
                              {subIndex > 0 && (
                                <Flex
                                  alignItems="center"
                                  color="accents.100"
                                  cursor="pointer"
                                  onClick={() => {
                                    const duplicate = JSON.parse(
                                      JSON.stringify(
                                        values?.chosenTransportationData?.[
                                          currentTransportation?.toString()
                                        ]?.nearbyTransValues
                                      )
                                    );
                                    setFieldValue(
                                      `chosenTransportationData.[${currentTransportation?.toString()}].nearbyTransValues`,
                                      [
                                        ...duplicate.slice(0, subIndex),
                                        ...duplicate.slice(subIndex + 1),
                                      ]
                                    );
                                  }}
                                >
                                  <MemoClose height="1.3rem" width="1.3rem" />
                                  {/* <Text
                                  color="accents.100"
                                  fontFamily="NeometricBold"
                                  ml="1rem"
                                >
                                  Remove
                                </Text> */}
                                </Flex>
                              )}
                            </Flex>

                            {/* chosenTransstructureData -> currentTransstructure?.toString() -> { nearbyTransValues:  [ {id ,images} ] } */}
                            <FormSelect
                              name={`chosenTransportationData.[${currentTransportation?.toString()}].nearbyTransValues.[${subIndex}].nearbyTransValueId`}
                              placeholder="Select drop down"
                              loadOptions={(inputString) =>
                                optionFetch(
                                  `/nearbytransvalues/get/type?type=${
                                    nearbyTransportation?.find(
                                      (i) => i?.value === currentTransportation
                                    )?.label
                                  }`,
                                  "name",
                                  "id",
                                  inputString,
                                  (res) => {
                                    return res?.filter(
                                      (resItem) =>
                                        !values?.chosenTransportationData?.[
                                          currentTransportation?.toString()
                                        ]?.nearbyTransValues?.find(
                                          (nearbyTransValue) =>
                                            nearbyTransValue?.nearbyTransValueId
                                              ?.value === resItem?.value
                                        )
                                    );
                                  }
                                )
                              }
                              filterOption={(option, inputValue) => {
                                return (
                                  !values?.chosenTransportationData?.[
                                    currentTransportation?.toString()
                                  ]?.nearbyTransValues?.find(
                                    (nearbyTransValue) =>
                                      nearbyTransValue?.nearbyTransValueId
                                        ?.value === option?.value
                                  ) &&
                                  option?.label
                                    ?.toLowerCase()
                                    ?.includes(
                                      (inputValue || "")?.toLowerCase()
                                    )
                                );
                              }}
                              isOptionLoadAsync
                              defaultOptions
                              cacheOptions
                            />
                            <FormInput
                              name={`chosenTransportationData.[${currentTransportation?.toString()}].nearbyTransValues.[${subIndex}].distanceToProject`}
                              // type="number"
                              placeholder="Enter in kms"
                              req
                            />
                          </Grid>
                          {/* <FileUpload
                            files={
                              values?.chosenTransportationData?.[
                                currentTransportation?.toString()
                              ]?.nearbyTransValues?.[subIndex]?.images
                            }
                            onUpload={(locations) => {
                              const newProjectImages = [
                                ...(values?.chosenTransportationData?.[
                                  currentTransportation?.toString()
                                ]?.nearbyTransValues?.[subIndex]?.images
                                  ? JSON.parse(
                                      JSON.stringify(
                                        values?.chosenTransportationData?.[
                                          currentTransportation?.toString()
                                        ]?.nearbyTransValues?.[subIndex]?.images
                                      )
                                    ) || []
                                  : []),
                                ...locations,
                              ];

                              setFieldValue(
                                `chosenTransportationData.[${currentTransportation?.toString()}].nearbyTransValues.[${subIndex}].images`,
                                newProjectImages
                              );
                            }}
                            onCross={(index) => {
                              const currProjectImages = [
                                ...(values?.chosenTransportationData?.[
                                  currentTransportation?.toString()
                                ]?.nearbyTransValues?.[subIndex]?.images
                                  ? JSON.parse(
                                      JSON.stringify(
                                        values?.chosenTransportationData?.[
                                          currentTransportation?.toString()
                                        ]?.nearbyTransValues?.[subIndex]?.images
                                      )
                                    ) || []
                                  : []),
                              ];
                              const total = currProjectImages?.length;

                              const newProjectImages = [
                                ...currProjectImages?.slice(0, index),
                                ...currProjectImages?.slice(index + 1, total),
                              ];

                              setFieldValue(
                                `chosenTransportationData.[${currentTransportation?.toString()}].nearbyTransValues.[${subIndex}].images`,
                                newProjectImages
                              );
                            }}
                          /> */}
                        </Box>
                      ))}

                      <Flex
                        alignItems="center"
                        color="secondary.700"
                        cursor="pointer"
                        onClick={() => {
                          const duplicateObj = JSON.parse(
                            JSON.stringify(
                              values?.chosenTransportationData?.[
                                currentTransportation?.toString()
                              ]
                            )
                          );

                          setFieldValue(
                            `chosenTransportationData.${currentTransportation?.toString()}.nearbyTransValues`,
                            [...duplicateObj?.nearbyTransValues, defSubTransObj]
                          );
                        }}
                      >
                        <MemoAdd height="1.3rem" width="1.3rem" />
                        <Text
                          color="secondary.700"
                          fontFamily="NeometricBold"
                          ml="1rem"
                        >
                          Add{" "}
                          {nearbyTransportation?.find(
                            (i) => i?.value === currentTransportation
                          )?.label || ""}
                        </Text>
                      </Flex>
                    </Grid>
                  )
                )}

              <Grid
                gridTemplateColumns="1fr 1fr"
                gridColumnGap="2.4rem"
                gridRowGap="1.6rem"
              >
                <Label text="Access road width" req />

                <Label text="Quality of access road" req />

                <FormInput
                  name="accessRoadWidth"
                  // type="number"
                  placeholder="Enter in Metres"
                  req
                />
                <Box h="fit-content" alignSelf="center">
                  <Grid
                    gridAutoFlow="column"
                    gridGap="2.4rem"
                    justifyContent="start"
                    alignItems="center"
                    // gridGap="3rem"
                    // mb="1rem"
                  >
                    <FormRadio
                      name="qualityOfAccessRoad"
                      label="Good"
                      value="good"
                    />
                    <FormRadio
                      name="qualityOfAccessRoad"
                      label="Average"
                      value="average"
                    />
                    <FormRadio
                      name="qualityOfAccessRoad"
                      label="Poor"
                      value="poor"
                    />
                  </Grid>
                  {touched?.qualityOfAccessRoad &&
                    errors?.qualityOfAccessRoad && (
                      <Error text={errors?.qualityOfAccessRoad} />
                    )}
                </Box>
              </Grid>

              <Box>
                <Label text="Upload Road Pictures" mb="1.6rem" req />
                <FileUpload
                  files={values?.roadImages || []}
                  onUpload={(locations) => {
                    const newProjectImages = [
                      ...(values?.roadImages
                        ? JSON.parse(JSON.stringify(values?.roadImages)) || []
                        : []),
                      ...locations?.map((i) => ({ image: i, alt: "" })),
                    ];

                    setFieldValue("roadImages", newProjectImages);
                  }}
                  onSubmitDescription={({ alt, file }) => {
                    setFieldValue(`roadImages.[${file?.index}].alt`, alt);
                  }}
                  onCross={(index) => {
                    const currProjectImages = [
                      ...(values?.roadImages
                        ? JSON.parse(JSON.stringify(values?.roadImages)) || []
                        : []),
                    ];
                    const total = currProjectImages?.length;

                    const newProjectImages = [
                      ...currProjectImages?.slice(0, index),
                      ...currProjectImages?.slice(index + 1, total),
                    ];

                    setFieldValue("roadImages", newProjectImages);
                  }}
                />
                {touched?.roadImages && errors?.roadImages && (
                  <ErrorMsg text={errors?.roadImages} />
                )}
              </Box>
              <Grid gridRowGap="1.6rem">
                <Label text="Comments (3 max)" req />
                <FormInput
                  name="connectivityComments"
                  placeholder="Enter Name"
                  type="textarea"
                  wrap="hard"
                  req
                />
              </Grid>

              <Flex my="2rem" gridGap="1.6rem">
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primaryOutline"
                  height="4.8rem"
                  onClick={onPrevious}
                >
                  Previous
                </Button>
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primary"
                  height="4.8rem"
                  type="submit"
                  loading={submitLoading}
                  onClick={() => {
                    setFieldValue("dirty", dirty);
                    submitForm();
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
